// Picks the auth screen from the URL params and the access state. Callback
// results render inside the door so the next action (Discord again, password)
// is one click away. Session expiry arrives through the client's 401 event.
import { useEffect, useState, useSyncExternalStore } from 'react';
import { subscribeSessionExpired } from '../../api/client';
import { postDiscordStart } from '../../api/endpoints';
import type { AccessState } from '../../api/hooks';
import { useMutation } from '@tanstack/react-query';
import { AccessChallenge } from './AccessChallenge';
import { CallbackStatus } from './CallbackStatus';
import { InvitationAccept } from './InvitationAccept';
import { ResetPassword } from './ResetPassword';
import { SessionExpired } from './SessionExpired';
import { SignInDoor } from './SignInDoor';
import { VerifyEmail } from './VerifyEmail';
import { parseAuthParams, stripAuthParams } from './authParams';

export interface AuthRouterProps {
  readonly access: AccessState;
  readonly accessError: unknown;
  readonly onRetryAccess: () => void;
  // True when a signed-in session dropped to the door in this page load.
  readonly hadSession: boolean;
}

function subscribeSearch(onChange: () => void): () => void {
  window.addEventListener('popstate', onChange);
  window.addEventListener('aleph:navigate', onChange);
  return () => {
    window.removeEventListener('popstate', onChange);
    window.removeEventListener('aleph:navigate', onChange);
  };
}
const readSearch = (): string => (typeof window === 'undefined' ? '' : window.location.search);

export function AuthRouter({ access, accessError, onRetryAccess, hadSession }: AuthRouterProps) {
  const search = useSyncExternalStore(subscribeSearch, readSearch, () => '');
  const params = parseAuthParams(search);
  const [expired, setExpired] = useState(hadSession);
  const [passwordHint, setPasswordHint] = useState<string | undefined>(undefined);
  // Set when a sign-in answered 409 or 428, or the callback carried one of the
  // two challenge statuses. The screen reads the challenge and finishes it.
  const [challenge, setChallenge] = useState<'workspace' | 'password' | null>(null);
  useEffect(() => subscribeSessionExpired(() => setExpired(true)), []);
  const discord = useMutation({ mutationFn: postDiscordStart, onSuccess: (data) => window.location.assign(data.authorizationUrl) });

  const done = () => stripAuthParams();
  const openChallenge = (kind: 'workspace' | 'password') => { stripAuthParams(); setChallenge(kind); };

  if (challenge !== null) return <AccessChallenge expected={challenge} onCancel={() => setChallenge(null)} />;

  if (params.screen === 'invitation' && params.invite) return <InvitationAccept token={params.invite} onDone={done} />;
  if (params.screen === 'verify' && params.verifyEmail) return <VerifyEmail token={params.verifyEmail} onDone={done} />;
  if (params.screen === 'reset' && params.resetPassword) return <ResetPassword param={params.resetPassword} onDone={done} />;

  const notice = params.screen === 'callback'
    ? (
      <CallbackStatus
        status={params.discordAuth}
        onDismiss={done}
        onUsePassword={() => { setPasswordHint('Signing in with email and password finishes the Discord link automatically.'); done(); }}
        onRetryDiscord={() => discord.mutate()}
        onOpenChallenge={openChallenge}
      />
    )
    : expired
      ? <SessionExpired onDismiss={() => setExpired(false)} />
      : undefined;

  return <SignInDoor access={access} accessError={accessError} onRetryAccess={onRetryAccess} notice={notice} passwordHint={passwordHint} onChallenge={openChallenge} />;
}
